import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { editarJson, enviarJson, pedirJson } from "./api";

/* Mural de avisos: a equipe publica, o cliente lê no painel até o aviso expirar.
   O contrato é o de `api/app/dominios/mural/`. */

export const CHAVE_DOS_AVISOS = ["avisos"] as const;

const uuid = z.string().uuid();

const esquemaAvisoDoCliente = z.object({
  id: uuid,
  titulo: z.string(),
  corpo: z.string(),
  inicio_em: z.string(),
  expira_em: z.string().nullable(),
});

const ESTADOS = ["agendado", "no_ar", "expirado", "arquivado"] as const;

const esquemaAvisoDaGestao = esquemaAvisoDoCliente.extend({
  todas_organizacoes: z.boolean(),
  organizacoes: z.array(z.object({ id: uuid, nome: z.string() })),
  estado: z.enum(ESTADOS),
  arquivado_em: z.string().nullable(),
  criado_por_nome: z.string().nullable(),
  criado_em: z.string(),
});

export type AvisoDoCliente = z.infer<typeof esquemaAvisoDoCliente>;
export type AvisoDaGestao = z.infer<typeof esquemaAvisoDaGestao>;
export type EstadoDoAviso = (typeof ESTADOS)[number];

export const ROTULO_DO_ESTADO_DO_AVISO: Record<EstadoDoAviso, string> = {
  agendado: "Agendado",
  no_ar: "No ar",
  expirado: "Expirado",
  arquivado: "Arquivado",
};

export interface FormularioDeAviso {
  titulo: string;
  corpo: string;
  todas: boolean;
  organizacaoIds: string[];
  /** `datetime-local` (sem fuso). Vazio = começa agora / não expira. */
  inicio: string;
  expira: string;
}

export const AVISO_EM_BRANCO: FormularioDeAviso = {
  titulo: "",
  corpo: "",
  todas: true,
  organizacaoIds: [],
  inicio: "",
  expira: "",
};

export function validarAviso(form: FormularioDeAviso, agora: Date = new Date()): string | null {
  if (!form.titulo.trim()) return "Dê um título ao aviso.";
  if (!form.corpo.trim()) return "Escreva o texto do aviso.";
  if (!form.todas && form.organizacaoIds.length === 0) {
    return "Escolha para quais organizações o aviso vale, ou marque todas.";
  }
  const inicio = form.inicio ? new Date(form.inicio) : null;
  const expira = form.expira ? new Date(form.expira) : null;
  if (expira && Number.isNaN(expira.getTime())) return "A data de expiração é inválida.";
  if (inicio && expira && expira <= inicio) return "O aviso precisa expirar depois de começar.";
  if (expira && expira <= agora) return "O aviso já nasceria expirado: escolha uma data futura.";
  return null;
}

/** O corpo da API. `datetime-local` vira ISO no fuso do navegador. */
export function corpoDoAviso(form: FormularioDeAviso): Record<string, unknown> {
  return {
    titulo: form.titulo.trim(),
    corpo: form.corpo.trim(),
    todas_organizacoes: form.todas,
    organizacao_ids: form.todas ? [] : form.organizacaoIds,
    inicio_em: form.inicio ? new Date(form.inicio).toISOString() : null,
    expira_em: form.expira ? new Date(form.expira).toISOString() : null,
  };
}

/** "válido até 30/09/2026 18:00" ou "sem data para acabar". */
export function vigenciaLegivel(aviso: Pick<AvisoDoCliente, "expira_em">): string {
  if (!aviso.expira_em) return "Sem data para acabar";
  const data = new Date(aviso.expira_em);
  if (Number.isNaN(data.getTime())) return "—";
  return `Vale até ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data)}`;
}

export function useAvisosDoPainel(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DOS_AVISOS, "painel"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/avisos", z.array(esquemaAvisoDoCliente), { signal }),
  });
}

export function useAvisosDaGestao(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DOS_AVISOS, "gestao"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/avisos/gestao", z.array(esquemaAvisoDaGestao), { signal }),
  });
}

export function usePublicarAviso() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) => enviarJson("/avisos", esquemaAvisoDaGestao, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_AVISOS }),
  });
}

export function useAlterarAviso() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      editarJson(`/avisos/${encodeURIComponent(id)}`, esquemaAvisoDaGestao, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_AVISOS }),
  });
}

export function useArquivarAviso() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/avisos/${encodeURIComponent(id)}/arquivamento`, esquemaAvisoDaGestao, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_AVISOS }),
  });
}
